import type {
  AdErrorEvent,
  AdProgressEvent,
  AdRequestEvent,
  ErrorEvent,
  MessageEvent,
  PositionEvent,
  PresentationModeEvent,
  QuartileEvent,
  TimeEvent,
  WebediaPlayer
} from '@webedia-services/webedia-player';

import getHostName from 'common/tools/network/getHostName';
import slugify from 'common/tools/string/slugify';

import { WEBEDIA_WEBSITES } from 'website/configuration/constants';
import type { PlayerCustomData } from 'website/module/player';

export type PlayerEvents =
  | AdErrorEvent
  | AdProgressEvent
  | AdRequestEvent
  | ErrorEvent
  | MessageEvent
  | PositionEvent
  | PresentationModeEvent
  | QuartileEvent
  | TimeEvent;

const NORMALIZED_FILE_TYPE_THEME: { [key: string]: string } = {
  bonus: 'emission',
  TV_PROGRAM: 'emission'
};

const NORMALIZED_VIDEO_THEME: { [key: string]: string } = {
  movie: 'cinema',
  series: 'series',
  person: 'person',
  program: 'program',
  show: 'show'
};
// Maps context (usually Symfony routes) to correct values (for .NET legacy reasons)
const NORMALIZED_VIDEO_DISPLAY_CONTEXT: { [key: string]: string } = {
  // Specials cases for embed player inside news
  newspage_folder: 'editorial_content_feature',
  newspage_slideshow: 'editorial_content_slideshow',
  newspage_news: 'editorial_content_news',
  // end
  newspage_article: 'editorial_content_news',
  newspage: 'editorial_content_news',
  news: 'editorial_content_news',
  guide_episodes_page: 'list_page', // This can be removed by changing displaycontext of episode guide embed,
  series: 'homepage_serie',
  movies: 'homepage_cinema',
  moviepage: 'movie_page',
  person: 'person_page',
  seriespage: 'series_page'
};

const PLAYER_ON_SCROLL_PARTNERS = [
  'ac_footer',
  'ac_footer_push',
  'ac_list',
  'ac_emergence_svod_footer',
  'ac_emergence_svod_list'
];
export const getVideoDisplayContext = (customData: PlayerCustomData) => {
  const context = customData.context ?? '';
  if (context === 'iblogvision') {
    const hostName = getHostName();
    return WEBEDIA_WEBSITES.find((site: string) => hostName.endsWith(site))
      ? 'embed_webedia'
      : 'embed_off_site';
  }

  return NORMALIZED_VIDEO_DISPLAY_CONTEXT[context]
    ? NORMALIZED_VIDEO_DISPLAY_CONTEXT[context]
    : context;
};

export const getVideoFormat = (customData: PlayerCustomData) => {
  const fileType = customData.file_type ?? '';
  // eslint-disable-next-line camelcase
  return NORMALIZED_FILE_TYPE_THEME[fileType] || fileType;
};

export const getVideoLaunch = (customData: PlayerCustomData) => {
  let launch = customData.launch ?? 'play';
  const name = customData.partner?.name;
  if (typeof name === 'string' && PLAYER_ON_SCROLL_PARTNERS.includes(name)) {
    launch = 'scroll-play';
  }
  return launch;
};
export const getPostrollVideoName = (webediaPlayer: WebediaPlayer) =>
  webediaPlayer.postroll?.getCurrentPostroll()?.xt_title ?? '';

export const getVideoPlayingTitle = (
  customData: PlayerCustomData,
  webediaPlayer: WebediaPlayer,
  _event: PlayerEvents,
  action?: string
) => {
  if (
    action === 'preview-overlay-appears' ||
    action === 'preview-overlay-close'
  ) {
    const nextPostrollVideoTitle =
      webediaPlayer.postroll?.getNextPostroll()?.xt_title ?? '';

    return nextPostrollVideoTitle.replace(/_/, '-').toLowerCase();
  }

  if (!webediaPlayer.postroll?.isPlayingPostrollVideo()) {
    return customData.trackingIdentifier ?? '';
  }

  const postrollVideoTitle = getPostrollVideoName(webediaPlayer);
  return postrollVideoTitle.toLowerCase();
};

export const getVideoRelatedEntity = (
  customData: PlayerCustomData,
  webediaPlayer: WebediaPlayer,
  _event: PlayerEvents,
  action?: string
) => {
  if (
    action === 'preview-overlay-appears' ||
    action === 'preview-overlay-close'
  ) {
    const nextPostroll = webediaPlayer.postroll?.getNextPostroll();
    const nextPostrollVidId = nextPostroll?.ref ?? '';
    const nextPostrollVidTitle = nextPostroll?.refTitle ?? '';
    return `${nextPostrollVidId}-${nextPostrollVidTitle}`;
  }
  if (!webediaPlayer.postroll?.isPlayingPostrollVideo()) {
    return customData.relatedEntityTrackingIdentifier ?? '';
  }
  const currentPostroll = webediaPlayer.postroll?.getCurrentPostroll();
  const postrollVidId = currentPostroll?.ref ?? '';
  const postrollVidTitle = currentPostroll?.refTitle ?? '';
  return `${postrollVidId}-${slugify(postrollVidTitle)}`;
};

export const getVideoTheme = (customData: PlayerCustomData) => {
  const { relatedEntityType, isHomeProduced } = customData;
  let resultEntityType = relatedEntityType;
  if (!resultEntityType) {
    return 'undefined';
  }
  if (relatedEntityType === 'program' && isHomeProduced) {
    resultEntityType = 'show';
  }
  return (
    NORMALIZED_VIDEO_THEME[resultEntityType.toLowerCase()] || resultEntityType
  );
};
